import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Modal from 'components/CncUI/Modal';
import Button from 'components/CncUI/Button';
import Alert from 'components/CncUI/Alert';
import Typography from 'components/CncUI/Typography';
import Link from 'components/CncUI/Link';
import UploadInput from './UploadInput';
import UploadedFile from './UploadedFile';
import SnackbarContext from 'context/Snackbar/Context';
import uploadMyFile from './utilities/uploadMyFile';
import removeMyFile from './utilities/removeMyFile';
import { validateFileExtension } from 'utilities/fileHandlingHelpers';
import Logger from 'utilities/logger';
import { getHoursPlusUrl } from 'constants/config/hoursPlus';
import useCountry from 'hooks/useCountry';

const useStyles = makeStyles(theme => ({
  dialog: {
    minHeight: '30vw',
  },
  container: {
    minWidth: '30vw',
  },
  button: {
    margin: theme.spacing(1.5),
  },
}));

const FileUploader = ({
  category,
  filesAddedHandler,
  previewFileHandler,
  onClose,
}) => {
  const logger = Logger('MyFiles.fileUploader');
  const classes = useStyles();
  const showNotification = useContext(SnackbarContext);
  const [documents, setDocuments] = useState({});
  const [disableSubmit, setDisableSubmit] = useState(false);
  const { country } = useCountry();
  const isCanada = country === 'CAN';

  useEffect(() => {
    if (documents.length === 0) return;

    const uploads = Object.values(documents).filter(
      doc => doc.state === 'SUCCESS',
    );
    const uploadingFiles = Object.values(documents).filter(
      doc => doc.state === 'LOADING',
    );
    setDisableSubmit(uploads.length === 0 || uploadingFiles.length > 0);
  }, [documents]);

  const onUpload = newDoc =>
    setDocuments(prevDocuments => ({
      ...prevDocuments,
      ...newDoc,
    }));

  const addFile = newFile => {
    if (!validateFileExtension(newFile.name)) {
      showNotification({
        type: 'error',
        show: true,
        message: 'File type not supported',
      });
      return;
    }

    const doc = {
      file: newFile,
      name: newFile.name,
      state: 'LOADING',
      category,
    };

    if (documents[newFile.name]) {
      showNotification({
        type: 'error',
        show: true,
        message: 'Duplicate files are not allowed.',
      });
    } else {
      setDocuments(documents => ({
        ...documents,
        [newFile.name]: doc,
      }));
      uploadMyFile(doc, onUpload);
    }
  };

  const removeFileHandler = async filename => {
    if (documents[filename]) {
      const { id: fileId } = documents[filename].myFile;
      try {
        await removeMyFile(fileId);
        showNotification({
          type: 'success',
          show: true,
          message: 'Your file was successfully deleted.',
        });
        setDocuments(documents => {
          let updatedDocuments = { ...documents };
          delete updatedDocuments[filename];
          return updatedDocuments;
        });
      } catch (error) {
        showNotification({
          type: 'error',
          show: true,
          message: 'There was a problem deleting this file.',
        });
      }
    }
  };

  const addDocumentsHandler = () => {
    filesAddedHandler();
    setDocuments({});
    onClose();
  };

  const cancelHandler = async () => {
    const filesCleanup = Object.values(documents)
      .filter(doc => doc.state === 'SUCCESS')
      .map(doc => doc.myFile)
      .map(myFile => removeMyFile(myFile.id));

    try {
      await Promise.all(filesCleanup);
      setDocuments({});
    } catch (error) {
      logger.error(error);
    }
    onClose();
  };

  return (
    <Modal
      open={true}
      onClose={cancelHandler}
      scroll={'paper'}
      aria-label="Uploader"
      data-test-id={`Uploader-root`}
      classes={{ paper: classes.dialog }}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
    >
      <Modal.Title warning onClose={onClose}>
        Upload Supporting Documents
      </Modal.Title>
      <Modal.Content>
        <Modal.ContentText>
          Please upload PDF, JPG or PNG files only, other types of files will
          not be accepted. Choose files from file selector by clicking on the{' '}
          <i>Browse for File</i> Button.
        </Modal.ContentText>
        <Grid container spacing={2}>
          {!isCanada && (
            <Grid item xs={12}>
              <Alert info fullWidth>
                <Typography variant="h6" style={{ margin: 0 }}>
                  Using Hours+?
                </Typography>
                <Typography variant="body1" style={{ marginTop: 4 }} short>
                  Please upload supporting documents you need processed with
                  your timecards directly in{' '}
                  <Link
                    href={getHoursPlusUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Hours+.
                  </Link>
                </Typography>
              </Alert>
            </Grid>
          )}
          {Object.values(documents).map(d => (
            <Grid item xs={12}>
              <UploadedFile
                document={d}
                previewFileHandler={previewFileHandler}
                removeFileHandler={removeFileHandler}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <UploadInput addFileHandler={addFile} />
          </Grid>
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Grid container spacing={2} justify="flex-end">
          <Grid item>
            <Button secondary onClick={cancelHandler}>
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button onClick={addDocumentsHandler} disabled={disableSubmit}>
              Confirm Upload
            </Button>
          </Grid>
        </Grid>
      </Modal.Actions>
    </Modal>
  );
};

export default FileUploader;
